import { useCallback, useMemo, useReducer } from "react";
import type { ProcessSection, ProcessStatus, ProcessWorkflow } from "../../api";
import type { StageDraft } from "./types";
import type { TransitionDraft } from "./TransitionRules";

/** The whole editable layout, as one document. */
export type Draft = {
  stages: StageDraft[];
  sections: Record<number, ProcessSection>;
  workflows: ProcessWorkflow[];
  /** Per-move lifecycle restrictions. Only the restricted moves are held --
   *  see components/process/TransitionRules.tsx. */
  transitions: TransitionDraft[];
  statuses: ProcessStatus[];
};

/**
 * How many edits back you can go.
 *
 * Deep enough that a session of dragging is fully reversible, shallow enough
 * that the history of a large process is not tens of megabytes of JSON: each
 * entry is a full copy of the draft, which is what makes undo a pointer move
 * rather than a set of inverse operations to get subtly wrong.
 */
const HISTORY_LIMIT = 100;

type State = {
  past: Draft[];
  present: Draft;
  future: Draft[];
  /** Serialised `present`, kept in step so `dirty` is a string compare. */
  serial: string;
  /** Serialised layout as the server last returned it. */
  savedSerial: string;
  /** What the last undoable edit was, for the undo tooltip. */
  lastLabel: string;
};

type Action =
  | { type: "edit"; apply: (draft: Draft) => void; label: string; merge?: string }
  | { type: "undo" }
  | { type: "redo" }
  | { type: "reset"; draft: Draft };

const clone = (draft: Draft): Draft => JSON.parse(JSON.stringify(draft));

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case "edit": {
      const next = clone(state.present);
      action.apply(next);
      const serial = JSON.stringify(next);
      // An edit that changes nothing must not consume an undo step. Dragging a
      // node back where it started, or retyping the same character, otherwise
      // fills the history with entries that appear to do nothing when undone.
      if (serial === state.serial) return state;

      // Typing into a name field fires per keystroke. Coalescing consecutive
      // edits that carry the same `merge` key means one undo takes back the
      // whole word rather than one letter of it.
      const coalesce =
        action.merge !== undefined &&
        action.merge === state.lastLabel &&
        state.past.length > 0;

      const past = coalesce
        ? state.past
        : [...state.past, state.present].slice(-HISTORY_LIMIT);

      return {
        past,
        present: next,
        future: [],
        serial,
        savedSerial: state.savedSerial,
        lastLabel: action.merge ?? action.label,
      };
    }

    case "undo": {
      if (state.past.length === 0) return state;
      const previous = state.past[state.past.length - 1];
      return {
        past: state.past.slice(0, -1),
        present: previous,
        future: [state.present, ...state.future].slice(0, HISTORY_LIMIT),
        serial: JSON.stringify(previous),
        savedSerial: state.savedSerial,
        lastLabel: "",
      };
    }

    case "redo": {
      if (state.future.length === 0) return state;
      const next = state.future[0];
      return {
        past: [...state.past, state.present].slice(-HISTORY_LIMIT),
        present: next,
        future: state.future.slice(1),
        serial: JSON.stringify(next),
        savedSerial: state.savedSerial,
        lastLabel: "",
      };
    }

    case "reset": {
      // Loading, discarding and saving all land here: the server's answer
      // becomes both the present and the new baseline, and the history is
      // dropped because undoing across a save would offer to restore a layout
      // the server has already replaced.
      const serial = JSON.stringify(action.draft);
      return {
        past: [],
        present: action.draft,
        future: [],
        serial,
        savedSerial: serial,
        lastLabel: "",
      };
    }

    default:
      return state;
  }
};

const initial = (draft: Draft): State => {
  const serial = JSON.stringify(draft);
  return { past: [], present: draft, future: [], serial, savedSerial: serial, lastLabel: "" };
};

/**
 * The draft layout, its edit history, and whether it differs from the server.
 *
 * Two things live here that used to live in the page component:
 *
 * **Undo/redo.** Every change to the layout already funnelled through a single
 * `edit()` that deep-copied the draft, so the history is the sequence of those
 * copies. That is worth doing *before* encouraging more editing by dragging:
 * a drag is much easier to perform by accident than a form submit, and a
 * canvas without undo makes an accidental drag feel like damage.
 *
 * **`dirty`.** The page computed it with a `JSON.stringify` of the entire draft
 * on every single render. Serialising once per *edit* instead -- which the
 * history has to do anyway to detect no-op edits -- makes it a string compare.
 */
export const useProcessDraft = (initialDraft?: Draft) => {
  const [state, dispatch] = useReducer(
    reducer,
    initialDraft ?? {
      stages: [], sections: {}, workflows: [], statuses: [], transitions: [],
    },
    initial
  );

  /**
   * Change the draft.
   *
   * `label` names the change for the undo tooltip. `merge`, when given,
   * coalesces this edit with the previous one carrying the same key, so
   * holding a key down in a text field is one undo step and not forty.
   */
  const edit = useCallback(
    (apply: (draft: Draft) => void, label = "Edit", merge?: string) =>
      dispatch({ type: "edit", apply, label, merge }),
    []
  );

  const reset = useCallback(
    (draft: Draft) => dispatch({ type: "reset", draft }),
    []
  );

  const undo = useCallback(() => dispatch({ type: "undo" }), []);
  const redo = useCallback(() => dispatch({ type: "redo" }), []);

  return useMemo(
    () => ({
      draft: state.present,
      edit,
      reset,
      undo,
      redo,
      canUndo: state.past.length > 0,
      canRedo: state.future.length > 0,
      /** What undo would take back, for the tooltip. Empty after an undo. */
      undoLabel: state.lastLabel,
      dirty: state.serial !== state.savedSerial,
    }),
    [state, edit, reset, undo, redo]
  );
};
